import React, { useState, useCallback } from 'react';
import { FiFile, FiX, FiUpload, FiAlertTriangle } from 'react-icons/fi';
import Button from '../Button/Button';
import ButtonSecondary from '../Button/ButtonSecondary';
import { ImportModal as Modal, ImportModalContent } from './styles';
import './_styles.import-modal.scss';

const ImportModal = ({
    visible,
    onClose,
    onImport,
    templateId,
    templateName
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const validateFile = (file) => {
        if (!file) return false;

        // Check file type
        if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
            setError('Please select a valid CSV file');
            return false;
        }

        // Check file size (limit to 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB');
            return false;
        }

        setError('');
        return true;
    };

    const handleFileSelect = useCallback((file) => {
        if (validateFile(file)) {
            setSelectedFile(file);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        handleFileSelect(file);
    };

    const handleBrowseClick = () => {
        document.getElementById('file-input').click();
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            setError('Please select a file to import');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            await onImport(templateId, selectedFile);
            handleClose();
        } catch (error) {
            console.error('Import error:', error);
            setError(error.response?.data?.error || 'Failed to import template');
            // Reset file input and selected file on error
            const fileInput = document.getElementById('file-input');
            if (fileInput) {
                fileInput.value = '';
            }
            setSelectedFile(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setError('');
        setIsDragOver(false);
        onClose();
    };

    const removeFile = () => {
        setSelectedFile(null);
        setError('');
    };

    return (
        <Modal style={{ display: visible ? 'block' : 'none' }}>
            <ImportModalContent
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <h3>Import Price Template</h3>
                <p className="template-info">Importing to: <strong>{templateName}</strong></p>

                <div className="file-upload-area">
                    <div
                        className={`drop-zone ${isDragOver ? 'drag-over' : ''} ${selectedFile ? 'has-file' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={handleBrowseClick}
                    >
                        {selectedFile ? (
                            <div className="file-info">
                                <div className="file-icon"><FiFile /></div>
                                <div className="file-details">
                                    <span className="file-name">{selectedFile.name}</span>
                                    <span className="file-size">
                                        {(selectedFile.size / 1024).toFixed(1)} KB
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className="remove-file"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile();
                                    }}
                                >
                                    <FiX />
                                </button>
                            </div>
                        ) : (
                            <div className="upload-prompt">
                                <div className="upload-icon"><FiUpload /></div>
                                <div className="upload-text">
                                    <p>Drag and drop your CSV file here</p>
                                    <p>or <button type="button" className="browse-button">browse</button></p>
                                </div>
                            </div>
                        )}
                    </div>

                    <input
                        id="file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                    />
                </div>

                <div className="requirements">
                    <h4>CSV Format Requirements:</h4>
                    <ul>
                        <li>Headers must be: <strong>Product</strong>, <strong>Price</strong></li>
                        <li>Product column: Product names (must match existing products)</li>
                        <li>Price column: Numeric prices</li>
                        <li>File format: .csv</li>
                    </ul>
                </div>

                {error && (
                    <div className="error-message">
                        <span className="error-icon"><FiAlertTriangle /></span>
                        {error}
                    </div>
                )}

                <div className="buttons">
                    <ButtonSecondary title="Cancel" onClick={handleClose} />
                    <Button
                        title={isLoading ? 'Importing...' : 'Import'}
                        onClick={handleSubmit}
                        disabled={isLoading || !selectedFile}
                    />
                </div>
            </ImportModalContent>
        </Modal>
    );
};

export default ImportModal;
